import React, { useState, useContext, useEffect, Fragment } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import { RemediationContext } from '../NRRemediationContext';
import StepperIndicator from '../NRRemediationContent/StepperIndicator';
import useNRMutation from '../../../graphql/UseNRMutation';
import NRButtonProgress from '../../NRButtonProgress/NRButtonProgress';
import { useSnackbar } from 'notistack';
import Divider from '@mui/material/Divider';
import { GridContext } from '../../NRReactGrid/NRReactGridContext';
import NRCryptographyRemediationStepOne from './NRCryptographyRemediationStepOne';
import NRCryptographyRemediationStepTwo from './NRCryptographyRemediationStepTwo';
import { useParams } from 'react-router-dom';
import { FiltersContext } from '../../../pages/NRReportTabs/NRReportFilterContextWrapper';

const steps = ['Status', 'Details'];

function NRCryptographyRemediationContent({
  inDrawer = false,
  items = [],
  mutation,
  mutationName,
  refetchQuery,
  onSubmitExtraActions = () => {},
  onResponseExtraActions = () => {},
  onCancelExtraActions = () => {},
  successMessage = 'Status changed successfully.',
  itemTypeLabel = 'Certificate',
  itemType = 'certificates',
  getCryptographicComponentId = (item, itemType = 'certificates') =>
    itemType === 'certificates' ? { filePath: item?.filePath, sha256: item?.sha256 } : { filePath: item?.filePath, matchHash: item?.matchHash },
  getItemStatus = item => item?.currentRemediation?.status ?? 'UNSPECIFIED',
  getItemRemediation = item => item?.currentRemediation,
  buildMutationArgs = (form, componentId) => ({
    assetId: componentId,
    details: form.description,
    ...(form[itemType]?.length > 0 && { [itemType]: form[itemType] }),
    status: form.status
  })
}) {
  const { setOpenRemediationModal, lastQueryCryptographyVariables = {} } = useContext(RemediationContext);
  const { setDrawerContent, drawerContent } = useContext(FiltersContext);
  const { componentId } = useParams();
  const { setShouldClearSelection } = useContext(GridContext);
  const { enqueueSnackbar } = useSnackbar();

  const [remediateItems, result, loading] = useNRMutation(mutation, mutationName, null, null, {
    refetchQueries: [{ query: refetchQuery, variables: lastQueryCryptographyVariables, context: { version: 'v3' } }],
    awaitRefetchQueries: true,
    onCompleted: (result, refetch) => {
      // TODO: Improve this once we have an actual response from the mutation
      if (inDrawer) {
        const newDrawerContent = [...drawerContent];
        setDrawerContent(newDrawerContent);
      }
    }
  });

  const emptyForm = {
    status: items?.length === 1 ? getItemStatus(items[0]) : 'UNSPECIFIED',
    description: items?.length === 1 ? getItemRemediation(items[0])?.detail || getItemRemediation(items[0])?.description || '' : '',
    [itemType]: items?.map(item => getCryptographicComponentId(item, itemType))
  };

  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: emptyForm
  });
  const { handleSubmit, watch, setValue } = methods;
  const form = watch();

  const onSubmitHandler = values => {
    onSubmitExtraActions(form);
    const mutationArgs = buildMutationArgs(values, componentId);
    remediateItems({
      context: { version: 'v3' },
      variables: {
        args: mutationArgs
      }
    });
  };

  useEffect(() => {
    if (result && Array.isArray(result) && result.length > 0) {
      const succeeded = result.filter(item => !item?.errorMessage);
      const failed = result.filter(item => item?.errorMessage);
      const succeededCount = succeeded.length;
      const failedCount = failed.length;

      if (failedCount === 0) {
        enqueueSnackbar(successMessage.replace('${0}', String(succeededCount)), { variant: 'success' });
      } else {
        if (succeededCount > 0) {
          enqueueSnackbar(successMessage.replace('${0}', String(succeededCount)), { variant: 'success' });
        }
        const failureSummary =
          failedCount === 1
            ? failed[0].errorMessage
            : `${failedCount} items failed: ${failed.map(f => f.errorMessage || 'Unknown error').join('; ')}`;
        enqueueSnackbar(failureSummary, { variant: 'error', autoHideDuration: null });
      }

      setOpenRemediationModal(false);
      onResponseExtraActions(form);
      setShouldClearSelection(true);
    }
  }, [result]);

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleCancel = () => {
    onCancelExtraActions();
    setOpenRemediationModal(false);
  };

  return (
    <Box {...(inDrawer && { height: '100%' })}>
      <FormProvider {...methods}>
        <form {...(inDrawer && { style: { height: '100%' } })} noValidate autoComplete="off" onSubmit={handleSubmit(onSubmitHandler)}>
          <Box
            width={'100%'}
            sx={{
              ...(inDrawer && {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              })
            }}
          >
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel icon={<StepperIndicator number={index} completed={activeStep > index} active={activeStep === index} />}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Fragment>
              {activeStep === 0 && <NRCryptographyRemediationStepOne />}
              {activeStep === 1 && <NRCryptographyRemediationStepTwo />}
              {inDrawer && <Divider sx={{ marginTop: 'auto', marginLeft: '-24px', marginRight: '-24px' }} />}
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button variant={'outlined'} color={'secondary'} onClick={() => handleCancel()} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button variant={'outlined'} color={'secondary'} disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                  Back
                </Button>
                {activeStep !== steps.length - 1 && (
                  <Button onClick={handleNext} variant={'contained'} color={'secondary'}>
                    Next
                  </Button>
                )}
                {activeStep === steps.length - 1 && (
                  <NRButtonProgress variant="contained" color="secondary" type={'submit'} disabled={loading} isLoading={loading} label={'Finish'} />
                )}
              </Box>
            </Fragment>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
}

export default NRCryptographyRemediationContent;
